import { useQuery, useQueryClient } from '@tanstack/react-query'
import { ReactNode, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

import { API } from '@/api'
import type { ServerACLL3Rule, ServerACLL4Rule } from '@/api/type-server'
import ArrowLeftIcon from '@/assets/icons/arrow-left.svg?react'
import InfoCircleIcon from '@/assets/icons/info-circle.svg?react'
import { Button } from '@/components/base/Button'
import { Divider } from '@/components/base/Divider'
import { Dropdown } from '@/components/base/Dropdown'
import { Input } from '@/components/base/Input'
import { Loading } from '@/components/base/Loading'
import { message } from '@/components/base/Message'
import { Radio } from '@/components/base/Radio'
import { Textarea } from '@/components/base/Textarea'
import { Trans } from '@/components/Trans'
import {
  getEIPACLPath,
  getEIPL4RuleCreatePath,
  getEIPL4RuleEditPath,
  getEIPListPath,
  useUnifiedContext,
} from '@/contexts/UnifiedContext'
import { ACLAction, ACLDirection, ACLProtocol } from '@/models/acl'
import { EIPBreadcrumb } from '@/pages/eip/components/EIPBreadcrumb'
import { cn } from '@/utils/style'

interface EIPL4RuleFormPageProps {
  mode: 'create' | 'edit'
}

type LocationState = {
  l3Rule?: ServerACLL3Rule
  rule?: ServerACLL4Rule
  template?: ServerACLL4Rule
}

type PortRange = { id: string; start: string; end: string }
type ProtocolOptionValue = 'custom-tcp' | 'custom-udp' | 'any-tcp' | 'any-udp' | 'any-icmpv4'
type PortValidation =
  | { kind: 'empty' }
  | { kind: 'incomplete' }
  | { kind: 'startRange' }
  | { kind: 'endRange' }
  | { kind: 'order'; startNum: number; endNum: number }
  | { kind: 'valid'; startNum: number; endNum: number }
type ValidPortValidation = Extract<PortValidation, { kind: 'valid' }>

function portErrorMessage(
  v: PortValidation | undefined,
  t: (key: string) => string,
): string | null {
  if (!v) return null
  switch (v.kind) {
    case 'incomplete':
      return t('aclRules.errorPortIncomplete')
    case 'startRange':
    case 'endRange':
      return t('aclRules.errorPortRange')
    case 'order':
      return t('aclRules.errorPortOrder')
    default:
      return null
  }
}

const ANY_PORT_START = '1'
const ANY_PORT_END = '65535'
const MAX_L4_RULES_PER_CREATE = 10

const PROTOCOL_FROM_LABEL: Record<string, ACLProtocol> = {
  ANY: ACLProtocol.ANY,
  ICMP: ACLProtocol.ICMP,
  TCP: ACLProtocol.TCP,
  UDP: ACLProtocol.UDP,
}

function parseProtocol(value: number | string): ACLProtocol {
  if (typeof value === 'number') return value as ACLProtocol
  return PROTOCOL_FROM_LABEL[value.toUpperCase()] ?? ACLProtocol.TCP
}

function protocolValueFromRule(rule?: ServerACLL4Rule): ProtocolOptionValue {
  const protocol = rule ? parseProtocol(rule.protocol) : ACLProtocol.TCP
  const isAnyPort =
    rule && String(rule.portStart) === ANY_PORT_START && String(rule.portEnd) === ANY_PORT_END

  if (protocol === ACLProtocol.ICMP) return 'any-icmpv4'
  if (protocol === ACLProtocol.UDP) return isAnyPort ? 'any-udp' : 'custom-udp'
  return isAnyPort ? 'any-tcp' : 'custom-tcp'
}

const PROTOCOL_OPTION_META: Record<
  ProtocolOptionValue,
  { protocol: ACLProtocol; portStart?: string; portEnd?: string }
> = {
  'custom-tcp': { protocol: ACLProtocol.TCP },
  'custom-udp': { protocol: ACLProtocol.UDP },
  'any-tcp': { protocol: ACLProtocol.TCP, portStart: ANY_PORT_START, portEnd: ANY_PORT_END },
  'any-udp': { protocol: ACLProtocol.UDP, portStart: ANY_PORT_START, portEnd: ANY_PORT_END },
  'any-icmpv4': { protocol: ACLProtocol.ICMP, portStart: ANY_PORT_START, portEnd: ANY_PORT_END },
}

const UNSUPPORTED_PROTOCOL_VALUES = new Set<string | number>(['any-icmpv4', 'any-icmpv6'])

let portRangeKey = 0
const newPortRange = (start = '', end = ''): PortRange => ({
  id: `pr-${++portRangeKey}`,
  start,
  end,
})

export function EIPL4RuleFormPage({ mode }: EIPL4RuleFormPageProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const ctx = useUnifiedContext()
  const { eipResId, l3RuleId, l4RuleId } = useParams<{
    eipResId: string
    l3RuleId: string
    l4RuleId?: string
  }>()
  const eipId = eipResId ?? ''
  const parentL3RuleId = l3RuleId ?? ''
  const eipListPath = getEIPListPath(ctx)
  const aclPath = getEIPACLPath(ctx, eipId)
  const isEdit = mode === 'edit'
  const navState = (location.state ?? {}) as LocationState

  const backToList = () => navigate(aclPath)
  const currentPath = isEdit
    ? getEIPL4RuleEditPath(ctx, eipId, parentL3RuleId, l4RuleId ?? '')
    : getEIPL4RuleCreatePath(ctx, eipId, parentL3RuleId)

  const { data: acl, isLoading: aclLoading } = useQuery({
    queryKey: ['eipACL', ctx.mode, ctx.teamResID, ctx.memberResID, eipResId],
    enabled: !!eipResId,
    queryFn: async ({ signal }) => {
      const res = await API.getACLResource({ net_res_id: eipResId! }, { signal })
      if (res.code !== 0) throw new Error(res.msg)
      return res.resource
    },
  })

  const aclResID = acl?.resID

  // Find parent L3 rule. Prefer state, otherwise lookup from list.
  const { data: fetchedL3, isLoading: l3Loading } = useQuery({
    queryKey: ['eipACLL3Rule', ctx.mode, ctx.teamResID, ctx.memberResID, aclResID, l3RuleId],
    enabled: !navState.l3Rule && !!aclResID && !!l3RuleId,
    queryFn: async ({ signal }) => {
      const ruleId = Number(l3RuleId)
      const lookup = async (direction: ACLDirection) => {
        const res = await API.getACLL3Rules(
          { acl_res_id: aclResID!, traffic_direction: direction, page: 1, per_page: 200 },
          { signal },
        )
        if (res.code !== 0) throw new Error(res.msg)
        return (res.l3Rules ?? []).find((r) => r.id === ruleId)
      }
      const found = (await lookup(ACLDirection.Inbound)) ?? (await lookup(ACLDirection.Outbound))
      if (!found) throw new Error('L3 rule not found')
      return found as ServerACLL3Rule
    },
  })

  const l3Rule = navState.l3Rule ?? fetchedL3

  // Edit mode: fetch L4 rule by id from the L3's L4 list.
  const { data: fetchedL4, isLoading: l4Loading } = useQuery({
    queryKey: [
      'eipACLL4Rule',
      ctx.mode,
      ctx.teamResID,
      ctx.memberResID,
      aclResID,
      l3RuleId,
      l4RuleId,
    ],
    enabled: isEdit && !navState.rule && !!aclResID && !!l3RuleId && !!l4RuleId,
    queryFn: async ({ signal }) => {
      const ruleId = Number(l4RuleId)
      const res = await API.getACLL4ExceptionRules(
        {
          acl_res_id: aclResID!,
          l3_rule_id: Number(l3RuleId),
          page: 1,
          per_page: 200,
        },
        { signal },
      )
      if (res.code !== 0) throw new Error(res.msg)
      const found = (res.l4Rules ?? []).find((r) => r.id === ruleId)
      if (!found) throw new Error('L4 rule not found')
      return found as ServerACLL4Rule
    },
  })

  const editRule = isEdit ? (navState.rule ?? fetchedL4) : undefined
  const isRuleLoading = isEdit && !navState.rule && l4Loading

  if (aclLoading || l3Loading || isRuleLoading) {
    return (
      <div className="card bg-panel-white h-full flex items-center justify-center">
        <Loading />
      </div>
    )
  }

  if (!aclResID || !l3Rule || (isEdit && !editRule)) {
    return (
      <div className="card bg-panel-white h-full overflow-y-auto">
        <FormHeader
          aclResID={acl?.resID ?? eipResId ?? ''}
          parentCIDR={l3Rule?.remoteCIDR ?? ''}
          eipListPath={eipListPath}
          aclPath={aclPath}
          currentPath={currentPath}
          isEdit={isEdit}
          onBack={backToList}
        />
        <Divider />
        <div className="mt-6 t-sm text-tertiary">
          <Trans ns="eip" i18nKey="aclRules.empty">
            No ACL rules.
          </Trans>
        </div>
      </div>
    )
  }

  return (
    <FormBody
      isEdit={isEdit}
      aclResID={aclResID}
      l3Rule={l3Rule}
      eipListPath={eipListPath}
      aclPath={aclPath}
      currentPath={currentPath}
      rule={editRule}
      template={!isEdit ? navState.template : undefined}
      onBack={backToList}
    />
  )
}

function FormHeader({
  aclResID,
  parentCIDR,
  eipListPath,
  aclPath,
  currentPath,
  isEdit,
  onBack,
}: {
  aclResID: string
  parentCIDR: string
  eipListPath: string
  aclPath: string
  currentPath: string
  isEdit: boolean
  onBack: () => void
}) {
  return (
    <div className="flex items-center gap-3">
      <button
        type="button"
        className="text-tertiary cursor-pointer hover:text-primary"
        onClick={onBack}
        aria-label="back"
      >
        <ArrowLeftIcon className="w-5 h-5" />
      </button>
      <EIPBreadcrumb
        items={[
          {
            label: (
              <Trans ns="eip" i18nKey="pageTitle">
                Access Point
              </Trans>
            ),
            to: eipListPath,
          },
          { label: aclResID, to: aclPath },
          ...(parentCIDR ? [{ label: parentCIDR, to: aclPath }] : []),
          {
            label: isEdit ? (
              <Trans ns="eip" i18nKey="aclRules.l4FormEditTitle">
                Edit L4 Rule
              </Trans>
            ) : (
              <Trans ns="eip" i18nKey="aclRules.l4FormNewTitle">
                New L4 Rule
              </Trans>
            ),
            to: currentPath,
            active: true,
          },
        ]}
      />
    </div>
  )
}

function FormBody({
  isEdit,
  aclResID,
  l3Rule,
  eipListPath,
  aclPath,
  currentPath,
  rule,
  template,
  onBack,
}: {
  isEdit: boolean
  aclResID: string
  l3Rule: ServerACLL3Rule
  eipListPath: string
  aclPath: string
  currentPath: string
  rule?: ServerACLL4Rule
  template?: ServerACLL4Rule
  onBack: () => void
}) {
  const { t } = useTranslation('eip')
  const ctx = useUnifiedContext()
  const queryClient = useQueryClient()

  const source = isEdit ? rule : template

  // L4 action is constrained by L3 default policy (whitelist/blacklist mode).
  const allowedAction: ACLAction =
    l3Rule.defaultAction === ACLAction.Deny ? ACLAction.Allow : ACLAction.Deny

  const [loading, setLoading] = useState(false)
  const [protocolValue, setProtocolValue] = useState<ProtocolOptionValue>(() =>
    protocolValueFromRule(source),
  )
  const [portRanges, setPortRanges] = useState<PortRange[]>(() => {
    if (source) {
      return [newPortRange(String(source.portStart), String(source.portEnd))]
    }
    return [newPortRange()]
  })
  const [description, setDescription] = useState(source?.description ?? '')

  const protocolOptions = useMemo(
    () => [
      { label: 'Custom TCP', value: 'custom-tcp', className: 't-sm' },
      { label: 'Custom UDP', value: 'custom-udp', className: 't-sm' },
      { label: 'Any TCP', value: 'any-tcp', className: 't-sm', separatorBefore: true },
      { label: 'Any UDP', value: 'any-udp', className: 't-sm' },
      {
        label: (
          <span className="flex w-full items-center gap-3">
            <span>Any ICMPv4</span>
            <span className="ml-auto px-2 py-1 rounded bg-neutral-50 text-tertiary t-xs">
              {t('aclRules.comingSoon')}
            </span>
          </span>
        ),
        value: 'any-icmpv4',
        className: 't-sm',
        separatorBefore: true,
        disabled: true,
      },
      {
        label: (
          <span className="flex w-full items-center gap-3">
            <span>Any ICMPv6</span>
            <span className="ml-auto px-2 py-1 rounded bg-neutral-50 text-tertiary t-xs">
              {t('aclRules.comingSoon')}
            </span>
          </span>
        ),
        value: 'any-icmpv6',
        className: 't-sm',
        disabled: true,
      },
    ],
    [t],
  )

  const groupOptions = useMemo(
    () => [{ label: t('aclRules.groupPort'), value: 'port', className: 't-sm' }],
    [t],
  )

  const selectedProtocol = PROTOCOL_OPTION_META[protocolValue]
  const isAnyProtocol = selectedProtocol.portStart !== undefined
  const editablePortRanges = useMemo(() => {
    if (!isAnyProtocol) return portRanges
    return [newPortRange(selectedProtocol.portStart, selectedProtocol.portEnd)]
  }, [isAnyProtocol, portRanges, selectedProtocol.portEnd, selectedProtocol.portStart])
  const portSummary = useMemo(() => {
    const portValidations: PortValidation[] = []
    const validPorts: ValidPortValidation[] = []
    let filledCount = 0
    let decomposedCount = 0

    for (const { start, end } of editablePortRanges) {
      const startEmpty = start === ''
      const endEmpty = end === ''
      if (startEmpty && endEmpty) {
        portValidations.push({ kind: 'empty' })
        continue
      }
      filledCount += 1
      if (startEmpty || endEmpty) {
        portValidations.push({ kind: 'incomplete' })
        continue
      }
      const startNum = Number(start)
      const endNum = Number(end)
      const startOk = Number.isInteger(startNum) && startNum >= 1 && startNum <= 65535
      const endOk = Number.isInteger(endNum) && endNum >= 1 && endNum <= 65535
      if (!startOk) {
        portValidations.push({ kind: 'startRange' })
        continue
      }
      if (!endOk) {
        portValidations.push({ kind: 'endRange' })
        continue
      }
      if (endNum < startNum) {
        portValidations.push({ kind: 'order', startNum, endNum })
        continue
      }

      const validPort = { kind: 'valid', startNum, endNum } as const
      portValidations.push(validPort)
      validPorts.push(validPort)
      decomposedCount += endNum - startNum + 1
    }

    return {
      allValid: validPorts.length > 0 && validPorts.length === filledCount,
      decomposedCount,
      filledCount,
      portValidations,
      validPorts,
    }
  }, [editablePortRanges])

  const { allValid, decomposedCount, filledCount, portValidations, validPorts } = portSummary
  const isUnsupportedProtocol = UNSUPPORTED_PROTOCOL_VALUES.has(protocolValue)
  const isCreateLimitExceeded = !isEdit && validPorts.length > MAX_L4_RULES_PER_CREATE
  const isCreateLimitReached = !isEdit && filledCount >= MAX_L4_RULES_PER_CREATE

  const lastValidation = portValidations[portValidations.length - 1]
  const canAddPortRange =
    !loading &&
    !isEdit &&
    !isAnyProtocol &&
    !isCreateLimitReached &&
    lastValidation?.kind === 'valid'
  const canSubmit = !loading && allValid && !isUnsupportedProtocol && !isCreateLimitExceeded

  const addPortRange = () => {
    if (!canAddPortRange) return
    setPortRanges((prev) => [...prev, newPortRange()])
  }
  const removePortRange = (id: string) =>
    setPortRanges((prev) => (prev.length <= 1 ? prev : prev.filter((r) => r.id !== id)))
  const updatePortRange = (id: string, patch: Partial<Pick<PortRange, 'start' | 'end'>>) =>
    setPortRanges((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)))

  const updateProtocol = (value: string | number | (string | number)[]) => {
    if (Array.isArray(value)) return
    if (UNSUPPORTED_PROTOCOL_VALUES.has(value)) return
    const nextValue = value as ProtocolOptionValue
    setProtocolValue(nextValue)
    const nextProtocol = PROTOCOL_OPTION_META[nextValue]
    if (nextProtocol.portStart !== undefined) {
      setPortRanges([newPortRange(nextProtocol.portStart, nextProtocol.portEnd)])
    } else if (isAnyProtocol) {
      setPortRanges([newPortRange()])
    }
  }

  const onSubmit = async () => {
    if (!canSubmit) return
    setLoading(true)
    try {
      if (isEdit && rule) {
        const first = validPorts[0]
        const res = await API.updateACLL4ExceptionRule({
          acl_res_id: aclResID,
          l4_rule_id: rule.id,
          version: rule.version,
          protocol: selectedProtocol.protocol,
          port_start: first.startNum,
          port_end: first.endNum,
          description: description.trim(),
        })
        if (res.code === 0) {
          message.success(t('common.message.success', { ns: 'translation' }))
          queryClient.invalidateQueries({
            queryKey: ['aclL4', ctx.mode, ctx.teamResID, ctx.memberResID, aclResID, l3Rule.id],
          })
          onBack()
        }
      } else {
        const res = await API.createACLL4ExceptionRule({
          acl_res_id: aclResID,
          l3_rule_id: l3Rule.id,
          protocol: selectedProtocol.protocol,
          ports: validPorts.map((p) => ({ portStart: p.startNum, portEnd: p.endNum })),
          description: description.trim() || undefined,
        })
        if (res.code === 0) {
          message.success(t('common.message.success', { ns: 'translation' }))
          queryClient.invalidateQueries({
            queryKey: ['aclL4', ctx.mode, ctx.teamResID, ctx.memberResID, aclResID, l3Rule.id],
          })
          onBack()
        }
      }
    } catch (e) {
      console.error(e)
    } finally {
      setLoading(false)
    }
  }

  const submitLabel = isEdit
    ? t('aclRules.btnSaveL4Rule')
    : filledCount > 1
      ? t('aclRules.btnCreateL4Rules')
      : t('aclRules.btnCreateL4Rule')

  return (
    <div className="card bg-panel-white h-full overflow-y-auto">
      <FormHeader
        aclResID={aclResID}
        parentCIDR={l3Rule.remoteCIDR}
        eipListPath={eipListPath}
        aclPath={aclPath}
        currentPath={currentPath}
        isEdit={isEdit}
        onBack={onBack}
      />
      <Divider className="mt-4 mb-8" />

      <div className="flex flex-col gap-8">
        <PolicyBanner cidr={l3Rule.remoteCIDR} l3DefaultAction={l3Rule.defaultAction} />

        <FieldGroup
          label={
            <Trans ns="eip" i18nKey="aclRules.fieldAction">
              Action
            </Trans>
          }
        >
          <div className="grid grid-cols-2 gap-2 w-[576px] max-w-full">
            <CardRadio
              checked={allowedAction === ACLAction.Allow}
              disabled={allowedAction !== ACLAction.Allow || loading}
              onSelect={() => {}}
              labelClassName={allowedAction === ACLAction.Allow ? 'text-active' : undefined}
            >
              <Trans ns="eip" i18nKey="aclRules.actionAllow">
                Allow
              </Trans>
            </CardRadio>
            <CardRadio
              checked={allowedAction === ACLAction.Deny}
              disabled={allowedAction !== ACLAction.Deny || loading}
              onSelect={() => {}}
              labelClassName={allowedAction === ACLAction.Deny ? 'text-error' : undefined}
            >
              <Trans ns="eip" i18nKey="aclRules.actionDeny">
                Deny
              </Trans>
            </CardRadio>
          </div>
        </FieldGroup>

        <div className="w-[516px] max-w-full">
          <FieldGroup
            label={
              <Trans ns="eip" i18nKey="aclRules.fieldProtocol">
                Protocol
              </Trans>
            }
          >
            <Dropdown
              options={protocolOptions}
              value={protocolValue}
              disabled={loading}
              onChange={updateProtocol}
              className="!h-11"
            />
          </FieldGroup>
        </div>

        <div className="flex flex-col gap-4">
          {editablePortRanges.map((range, idx) => {
            const isLast = idx === editablePortRanges.length - 1
            const validation = portValidations[idx]
            const errorMessage = portErrorMessage(validation, t)
            const startInvalid =
              validation?.kind === 'incomplete'
                ? range.start === ''
                : validation?.kind === 'startRange' || validation?.kind === 'order'
            const endInvalid =
              validation?.kind === 'incomplete'
                ? range.end === ''
                : validation?.kind === 'endRange' || validation?.kind === 'order'
            return (
              <div key={range.id} className="flex flex-col gap-1">
                <div className="grid grid-cols-[120px_minmax(0,1fr)_88px_88px] gap-4 items-end">
                  <FieldGroup
                    label={
                      idx === 0 ? (
                        <Trans ns="eip" i18nKey="aclRules.fieldGroup">
                          Group
                        </Trans>
                      ) : null
                    }
                  >
                    <Dropdown
                      options={groupOptions}
                      value="port"
                      disabled
                      onChange={() => {}}
                      className="!h-11"
                    />
                  </FieldGroup>
                  <FieldGroup
                    label={
                      idx === 0 ? (
                        <Trans ns="eip" i18nKey="aclRules.fieldDestinationPort">
                          Destination Port
                        </Trans>
                      ) : null
                    }
                  >
                    <PortRangeInput
                      range={range}
                      committed={!isEdit && !isLast && !isAnyProtocol}
                      disabled={loading || isAnyProtocol}
                      startInvalid={startInvalid}
                      endInvalid={endInvalid}
                      onChange={(patch) => updatePortRange(range.id, patch)}
                      onRemove={() => removePortRange(range.id)}
                    />
                  </FieldGroup>
                  <Button
                    type="outline"
                    className="min-w-[88px] h-11"
                    disabled={loading || isAnyProtocol || editablePortRanges.length <= 1}
                    onClick={() => removePortRange(range.id)}
                  >
                    <Trans ns="eip" i18nKey="aclRules.portRangeRemove">
                      Remove
                    </Trans>
                  </Button>
                  {!isEdit && isLast ? (
                    <Button
                      type="secondary"
                      className="min-w-[88px] h-11"
                      disabled={!canAddPortRange}
                      onClick={addPortRange}
                    >
                      <Trans ns="eip" i18nKey="aclRules.portRangeAdd">
                        Add
                      </Trans>
                    </Button>
                  ) : (
                    <span className="w-[88px]" />
                  )}
                </div>
                {errorMessage && (
                  <div className="grid grid-cols-[120px_minmax(0,1fr)_88px_88px] gap-4">
                    <span />
                    <div className="t-sm text-red-500">{errorMessage}</div>
                  </div>
                )}
              </div>
            )
          })}
          {decomposedCount > 0 && (
            <div className="t-sm text-highlight">
              {t('aclRules.portDecomposeHint', { count: decomposedCount })}
            </div>
          )}
          {isCreateLimitReached && (
            <div className="t-sm text-tertiary">
              {t('aclRules.l4CreateLimitHint', { count: MAX_L4_RULES_PER_CREATE })}
            </div>
          )}
          {isCreateLimitExceeded && (
            <div className="t-sm text-red-500">
              {t('aclRules.l4CreateLimitError', { count: MAX_L4_RULES_PER_CREATE })}
            </div>
          )}
        </div>

        <FieldGroup
          required={false}
          label={
            <Trans ns="eip" i18nKey="aclRules.fieldDescription">
              Description
            </Trans>
          }
        >
          <Textarea
            value={description}
            disabled={loading}
            placeholder={t('aclRules.placeholderDescription')}
            onChange={(e) => setDescription(e.target.value)}
            maxLength={100}
            className="min-h-[88px] !border border-neutral-200 t-sm placeholder:text-tertiary"
          />
        </FieldGroup>
      </div>

      <div className="flex items-center justify-end gap-4 pt-4 mt-4">
        <Button
          type="outline"
          onClick={() => {
            if (!loading) onBack()
          }}
          className="min-w-[88px]"
        >
          <Trans ns="eip" i18nKey="aclRules.cancel">
            Cancel
          </Trans>
        </Button>
        <Button type="secondary" disabled={!canSubmit} onClick={onSubmit} className="min-w-[120px]">
          {submitLabel}
        </Button>
      </div>
    </div>
  )
}

function PolicyBanner({ cidr, l3DefaultAction }: { cidr: string; l3DefaultAction: number }) {
  const i18nKey =
    l3DefaultAction === ACLAction.Deny ? 'aclRules.l4PolicyAllowOnly' : 'aclRules.l4PolicyDenyOnly'
  return (
    <div className="flex items-center gap-4 p-4 rounded-lg bg-highlight/10 border border-highlight/30 text-highlight">
      <InfoCircleIcon className="w-5 h-5 shrink-0" />
      <span className="t-sm">
        <Trans ns="eip" i18nKey={i18nKey} values={{ cidr }}>
          {`L4 Rule belongs to L3 rule ${cidr}.`}
        </Trans>
      </span>
    </div>
  )
}

function PortRangeInput({
  range,
  committed,
  disabled,
  startInvalid,
  endInvalid,
  onChange,
  onRemove,
}: {
  range: PortRange
  committed?: boolean
  disabled?: boolean
  startInvalid?: boolean
  endInvalid?: boolean
  onChange: (patch: Partial<Pick<PortRange, 'start' | 'end'>>) => void
  onRemove?: () => void
}) {
  const display =
    range.end && range.end !== range.start ? `${range.start}-${range.end}` : range.start

  if (committed) {
    return (
      <div className="flex items-center gap-2 px-3 h-11 rounded-lg border border-neutral-200">
        <span className="inline-flex items-center gap-2 px-2 py-1 rounded bg-neutral-100 text-primary t-x-sb">
          {display}
          <button
            type="button"
            className="text-tertiary hover:text-primary"
            onClick={onRemove}
            aria-label="remove"
          >
            ×
          </button>
        </span>
      </div>
    )
  }

  return (
    <div className="flex items-center gap-2">
      <Input
        value={range.start}
        placeholder="1"
        disabled={disabled}
        className={cn(
          'h-11 leading-[44px] !border t-sm placeholder:text-tertiary',
          startInvalid ? '!border-red-500' : 'border-neutral-200',
        )}
        inputMode="numeric"
        onChange={(e) =>
          onChange({ start: e.target.value.replace(/[^0-9]/g, '').replace(/^0+(?=\d)/, '') })
        }
      />
      <span className="text-tertiary">-</span>
      <Input
        value={range.end}
        placeholder={range.start || '1'}
        disabled={disabled}
        className={cn(
          'h-11 leading-[44px] !border t-sm placeholder:text-tertiary',
          endInvalid ? '!border-red-500' : 'border-neutral-200',
        )}
        inputMode="numeric"
        onChange={(e) =>
          onChange({ end: e.target.value.replace(/[^0-9]/g, '').replace(/^0+(?=\d)/, '') })
        }
      />
    </div>
  )
}

function FieldGroup({
  label,
  required = true,
  children,
}: {
  label: ReactNode
  required?: boolean
  children: ReactNode
}) {
  return (
    <div>
      {label && (
        <div className={cn('mb-2 t-sb text-primary', required && 'text-with-star')}>{label}</div>
      )}
      {children}
    </div>
  )
}

function CardRadio({
  checked,
  disabled,
  onSelect,
  children,
  labelClassName,
}: {
  checked: boolean
  disabled?: boolean
  onSelect: () => void
  children: ReactNode
  labelClassName?: string
}) {
  return (
    <button
      type="button"
      onClick={() => {
        if (!disabled) onSelect()
      }}
      disabled={disabled}
      className={cn(
        'flex items-center gap-2 px-4 h-11 rounded-lg border transition-colors text-left bg-panel-white',
        checked ? 'border-gray300' : 'border-neutral-200 hover:border-neutral-300',
        disabled && !checked && 'cursor-not-allowed',
        disabled && checked && 'cursor-default',
      )}
    >
      <Radio checked={checked} disabled={disabled} className="pointer-events-none" />
      <span
        className={cn(
          't-sm',
          disabled && !checked ? 'text-tertiary' : 'text-primary',
          labelClassName,
        )}
      >
        {children}
      </span>
    </button>
  )
}
